import { useState } from 'react';
import { BookOpen, Check, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useCoachLessons, customLessonId, type CoachLesson } from '@/hooks/useCoachLessons';
import { LessonContentEditor } from './LessonContentEditor';

type Props = {
  coachId: string;
  teamId: string;
  sport: string;
  /** Lesson ids (already prefixed) currently selected for assignment. */
  selectedLessonIds: string[];
  onToggleLesson: (lessonId: string) => void;
  rosterPlayers?: { user_id: string; username: string | null }[];
};

/**
 * Coach-authored lessons. A lesson only needs a title — content of any kind
 * (video, article, note/resource) is optional and can be added later.
 */
export function CoachLessonBuilder({
  coachId,
  teamId,
  sport,
  selectedLessonIds,
  onToggleLesson,
  rosterPlayers,
}: Props) {
  const { lessons, loading, createLesson, deleteLesson } = useCoachLessons(coachId);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Give your lesson a title');
      return;
    }
    setSaving(true);
    try {
      const created = (await createLesson({ title, description, sport, teamId })) as CoachLesson | null;
      toast.success('Lesson created — add content any time');
      setTitle('');
      setDescription('');
      setCreating(false);
      if (created) setExpanded(created.id);
    } catch {
      toast.error('Could not create lesson');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteLesson(id);
      toast.success('Lesson deleted');
    } catch {
      toast.error('Could not delete lesson');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider">
          My lessons ({lessons.length})
        </p>
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setCreating((c) => !c)}>
          <Plus className="w-3 h-3 mr-1" /> {creating ? 'Cancel' : 'Create lesson'}
        </Button>
      </div>

      {creating && (
        <div className="rounded-lg border border-primary/30 p-2.5 space-y-2">
          <Input
            placeholder="Lesson title (e.g. Introduction to Soccer)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="font-body"
          />
          <Textarea
            placeholder="Instructions / what players should learn (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="font-body"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground font-body">Content is optional — you can save an empty lesson.</span>
            <Button size="sm" onClick={submit} disabled={saving} className="font-display font-bold">
              {saving ? 'Saving…' : 'Save lesson'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[11px] text-muted-foreground py-1">Loading your lessons…</p>
      ) : lessons.length === 0 ? (
        <p className="text-[11px] text-muted-foreground py-1">
          No lessons of your own yet. Create one above, or assign from the curriculum below.
        </p>
      ) : (
        <div className="space-y-1">
          {lessons.map((l) => {
            const fullId = customLessonId(l.id);
            const selected = selectedLessonIds.includes(fullId);
            const open = expanded === l.id;
            return (
              <div key={l.id} className="rounded-lg border border-border overflow-hidden">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <button
                    onClick={() => onToggleLesson(fullId)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    title="Select for assignment"
                  >
                    {selected ? (
                      <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                    ) : (
                      <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    )}
                    <span className={`text-xs truncate ${selected ? 'text-primary' : ''}`}>{l.title}</span>
                  </button>
                  <button onClick={() => setExpanded(open ? null : l.id)} className="shrink-0" aria-label="Edit lesson content">
                    {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  <button onClick={() => remove(l.id)} className="shrink-0" aria-label="Delete lesson">
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
                {open && (
                  <div className="border-t border-border bg-muted/10 px-2 py-2">
                    {l.description && (
                      <p className="text-[11px] text-muted-foreground font-body mb-2 whitespace-pre-wrap">{l.description}</p>
                    )}
                    <LessonContentEditor
                      lessonId={fullId}
                      coachId={coachId}
                      teamId={teamId}
                      defaultSport={sport as any}
                      rosterPlayers={rosterPlayers}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
